<template lang="pug">
	.ai-chat-voice-training(v-if="showed" v-window)
		.card
			.card-header
				i.fa.fa-microphone
				span Voice activation training
			.card-body.collapse.show
				.description.text-muted
					| Record the start phrase, the stop phrase and some background noise several times (3-30 recordings of each are recommended),
					| then train the model and test it. Recordings are used only for training and are discarded after it. Every trained model is saved, so you can switch between them.
					| In the chat, say the start phrase to begin a voice message and the stop phrase (or stay silent for 5 seconds) to send it.
				//- Состояние загрузки базовой модели Speech Commands / ошибки
				.voice-training-status(v-if="loading")
					i.fa.fa-spinner.fa-spin
					span Loading speech model...
				.voice-training-status.error(v-else-if="error")
					i.fa.fa-exclamation-triangle
					span {{error}}
				.voice-training-phrases.separator(v-if="!loading")
					//- Блок каждой фразы: заголовок, кнопка записи, список записей и индикатор распознавания при тестировании
					.voice-training-phrase(v-for="phrase in phrases" :key="phrase.label" :class="{detected: detectedLabel === phrase.label, recording: recordingLabel === phrase.label}")
						.voice-training-phrase-header
							.voice-training-phrase-title
								span {{phrase.title}}
								small.text-muted {{phrase.description}}
							.voice-training-phrase-count {{examples[phrase.label].length}} / {{maxExamples}}
							button.btn.btn-sm.btn-primary.voice-training-record-button(type="button" :disabled="busy || isLimitReached(phrase.label)" v-on:click="record(phrase.label)" v-b-tooltip.hover="{delay: { show: 500, hide: 100 }, title: recordingLabel === phrase.label ? 'Speak now...' : isLimitReached(phrase.label) ? `Maximum ${maxExamples} recordings. Delete one to record a new one` : `Record ${phrase.title.toLowerCase()} (about 1 second)`}")
								i.fa(:class="recordingLabel === phrase.label ? 'fa-circle' : 'fa-microphone'")
								span {{recordingLabel === phrase.label ? 'Speak now...' : 'Record'}}
						//- Записи фразы: клик - включить/выключить запись в обучении, крестик - удалить
						.voice-training-examples(v-if="examples[phrase.label].length")
							.voice-training-example(v-for="(example, index) in examples[phrase.label]" :key="example.id" :class="{disabled: example.enabled === false}" v-on:click="toggleExample(example)" v-b-tooltip.hover="{delay: { show: 500, hide: 100 }, title: `${example.enabled === false ? 'Excluded from training' : 'Used in training'} · ${formatDate(example.date)}. Click to ${example.enabled === false ? 'include' : 'exclude'}`}")
								i.fa(:class="example.enabled === false ? 'fa-square-o' : 'fa-check-square-o'")
								span \#{{index + 1}}
								i.fa.fa-times.voice-training-example-delete(v-on:click.stop="deleteExample(phrase.label, example)" v-b-tooltip.hover="{delay: { show: 500, hide: 100 }, title: 'Delete recording'}")
						.voice-training-examples-empty(v-else) No recordings yet
						//- Уровень уверенности модели во время тестирования
						.voice-training-score(v-if="testing")
							.voice-training-score-bar(:style="{width: `${Math.round((scores[phrase.label] || 0) * 100)}%`}")
							span {{Math.round((scores[phrase.label] || 0) * 100)}}%
				//- Обучение модели и тестирование
				.voice-training-model(v-if="!loading")
					.voice-training-model-info
						template(v-if="training")
							i.fa.fa-spinner.fa-spin
							span Training... epoch {{trainingProgress.epoch}} / {{trainingProgress.epochs}}
							span.text-muted(v-if="trainingProgress.accuracy !== null") &nbsp;· accuracy {{Math.round(trainingProgress.accuracy * 100)}}%
						template(v-else-if="selectedModel && !selectedModelLoaded")
							i.fa.fa-spinner.fa-spin
							span Loading selected model...
						template(v-else-if="models.length")
							i.fa.fa-check.color-green
							span Trained models: {{models.length}} / {{maxModels}}
						template(v-else)
							i.fa.fa-info-circle
							span Model is not trained yet
					//- Список обученных моделей: выбранная модель используется в чате и при тестировании
					.voice-training-model-select.separator(v-if="models.length")
						InputSelect(v-on:change="selectModel($event)" :default="selectedModelId" :value="selectedModelId" :values="modelItems" :options="{editable: false, dropdownWidth: 360, displayedValueTransform: v => getModelTitle(getModel(v))}" theme="dark-medium")
						button.btn.btn-danger.voice-training-model-delete(type="button" :disabled="busy || !selectedModel || !isModelSaved(selectedModel)" v-on:click="deleteModel($event)" v-b-tooltip.hover="{delay: { show: 500, hide: 100 }, title: 'Delete selected model'}")
							i.fa.fa-trash
					.voice-training-model-actions
						button.btn.btn-success(type="button" :disabled="busy || !canTrain || modelsLimitReached" v-on:click="train()" v-b-tooltip.hover="{delay: { show: 500, hide: 100 }, title: modelsLimitReached ? `Maximum ${maxModels} models. Delete one to train a new one` : 'Train a new model on the recordings'}")
							i.fa(:class="training ? 'fa-spinner fa-spin' : 'fa-cogs'")
							span &nbsp;&nbsp;{{training ? 'Training...' : 'Train model'}}
						button.btn(type="button" :class="testing ? 'btn-danger' : 'btn-primary'" :disabled="(busy && !testing) || !selectedModel" v-on:click="toggleTest()")
							i.fa(:class="testing ? 'fa-stop' : 'fa-headphones'")
							span &nbsp;&nbsp;{{testing ? 'Stop test' : 'Test'}}
						span.voice-training-test-hint.text-muted(v-if="testing") Speak freely - the phrase you say will be highlighted
						button.btn.btn-dark(type="button" v-on:click="closeWindow()") Close
</template>

<script>
	import _ 					from 'lodash'

	import Screen 				from './../../mixins/screen'

	import { AIAgent } 			from './../../services/AIAgent'
	import { Server } 			from './../../services/Server'
	import { VOICE_LABELS } 	from './../../services/AIAgent.VoiceInput'

	import InputSelect			from './../Editor/Controls/InputSelect'

	// Время подсветки распознанной фразы при тестировании (мс)
	const DETECTION_HIGHLIGHT_TIME = 1200;
	// Минимальная уверенность модели для подсветки фразы при тестировании
	const DETECTION_SCORE_THRESHOLD = 0.8;
	// Задержка перед началом обучения (мс): даём браузеру отрисовать состояние кнопки до тяжёлых вычислений
	const TRAIN_START_DELAY = 50;

	export default {
		name: "AIAgentVoiceTraining",
		mixins: [Screen],
		screen: {
			updateOn: [],
			events: {
				// Список моделей, выбор или содержимое модели пришли с сервера либо изменились локально
				"AI Chat Voice Models Changed": function() {

					this.refreshModels();

				},
				"AI Chat Voice Model Save Failed": function() {

					this.error = 'Saving the trained model failed';

				}
			}
		},
		components: {
			InputSelect
		},
		data() {

			this.phrases = [
				{label: VOICE_LABELS.start, title: 'Start phrase', description: 'Say it to start recording a voice message'},
				{label: VOICE_LABELS.stop, title: 'Stop phrase', description: 'Say it to finish and send the voice message'},
				{label: VOICE_LABELS.noise, title: 'Background noise', description: 'Stay silent (or let the usual room noise be recorded)'}
			];

			this.maxExamples = AIAgent.getMaxExamplesPerLabel();
			this.maxModels = AIAgent.getMaxVoiceModels();

			// Распознаватель для записи примеров фраз (создаётся при открытии окна)
			this.transferRecognizer = null;
			// Распознаватель тестируемой модели
			this.testRecognizer = null;
			this.detectionTimeout = null;

			return {
				// Обученные модели пользователя (без содержимого) и выбранная модель. Содержимое загружается только при переключении на модель
				models: [],
				selectedModelId: null,
				selectedModelLoaded: false,
				// Записи фраз - только на клиенте до начала обучения, после обучения удаляются
				examples: AIAgent.createEmptyExamples(),
				loading: false,
				error: null,
				recordingLabel: null,
				training: false,
				trainingProgress: {epoch: 0, epochs: 0, accuracy: null},
				testing: false,
				scores: {},
				detectedLabel: null
			};

		},
		computed: {
			busy() {

				return this.loading || !!this.recordingLabel || this.training || this.testing;

			},
			// Для обучения нужны включённые записи хотя бы старт и стоп фраз
			canTrain() {

				let examplesByLabel = AIAgent.getTrainingExamples(this.examples);

				return examplesByLabel[VOICE_LABELS.start].length > 0 && examplesByLabel[VOICE_LABELS.stop].length > 0;

			},
			modelsLimitReached() {

				return this.models.length >= this.maxModels;

			},
			selectedModel() {

				return this.getModel(this.selectedModelId);

			},
			// Элементы списка моделей (новые сверху)
			modelItems() {

				return _.map(_.reverse([...this.models]), model => {
					return {title: this.getModelTitle(model), description: this.getModelDescription(model), data: {itemValue: model.id}};
				});

			},
			// Состояние, от которого зависят доступность и подсказки кнопок окна
			buttonsState() {

				return [this.busy, this.recordingLabel, this.training, this.testing, this.selectedModelId, this.models.length, ..._.map(this.examples, examples => examples.length)].join('|');

			}
		},
		watch: {
			showed(showed) {

				if (!showed) {
					this.hideTooltips();
					this.cleanup();
				}

			},
			// Отключённая кнопка не получает mouseleave, поэтому показанная подсказка не скрывается сама - скрываем при смене статуса кнопок
			buttonsState() {

				this.hideTooltips();

			}
		},
		beforeDestroy() {

			this.hideTooltips();
			this.cleanup();

		},
		methods: {
			collectData(reason) {

				if (reason !== 'show') return;

				// Режим прослушивания в чате использует микрофон и ту же модель - на время обучения выключаем
				AIAgent.stopWakeWordMode();

				// При открытии дашборда клиент получает только выбранную модель - дозагружаем список остальных (без их содержимого)
				Server.sendToServer({
					LoadUserVoices: {}
				});

				this.refreshModels();
				this.examples = AIAgent.createEmptyExamples();
				this.error = null;
				this.scores = {};
				this.detectedLabel = null;

				this.loadRecognizer();

			},
			// Скрывает все показанные подсказки (v-b-tooltip)
			hideTooltips() {

				this.$root.$emit('bv::hide::tooltip');

			},
			async loadRecognizer() {

				this.loading = true;

				try {

					this.transferRecognizer = await AIAgent.createTransferRecognizer();

				} catch (e) {

					this.error = `Speech model loading failed: ${e?.message || e}`;

				}

				this.loading = false;

			},
			// Достигнут лимит записей фразы - новые записи возможны только после удаления существующих
			isLimitReached(label) {

				return this.examples[label].length >= this.maxExamples;

			},
			// Записывает один пример фразы (около 1 секунды). Существующие записи не удаляются - при достижении лимита запись недоступна
			async record(label) {

				if (this.busy || !this.transferRecognizer || this.isLimitReached(label)) return;

				this.recordingLabel = label;
				this.error = null;

				try {

					let example = await AIAgent.collectExample(this.transferRecognizer, label);

					this.examples[label].push(example);

				} catch (e) {

					this.error = `Recording failed: ${e?.message || e}`;

				}

				this.recordingLabel = null;

			},
			toggleExample(example) {

				this.$set(example, 'enabled', example.enabled === false);

			},
			deleteExample(label, example) {

				this.examples[label] = _.without(this.examples[label], example);

			},
			refreshModels() {

				this.models = [...AIAgent.getVoiceModels()];
				this.selectedModelId = AIAgent.selectedVoiceModelId;
				this.selectedModelLoaded = AIAgent.isVoiceModelLoaded(this.selectedModelId);

			},
			getModel(modelId) {

				return _.find(this.models, model => model.id === modelId) || null;

			},
			// Модель уже сохранена в БД (до ответа сервера у новой модели временный строковый id)
			isModelSaved(model) {

				return _.isNumber(model.id);

			},
			getModelTitle(model) {

				return model ? `Model ${this.formatDate(model.date)}` : '';

			},
			// Количество записей каждой фразы, на которых обучена модель
			getModelDescription(model) {

				let examplesCount = model.examplesCount || {};

				return _.map(this.phrases, phrase => `${phrase.title.toLowerCase()}: ${examplesCount[phrase.label] || 0}`).join(' · ');

			},
			// Переключение модели происходит сразу на клиенте, сервер только запоминает выбор.
			// Содержимое модели запрашивается только если его ещё нет на клиенте (уже загруженные модели остаются в кэше)
			selectModel(modelId) {

				let model = this.getModel(modelId);

				if (!model || modelId === this.selectedModelId) return;

				if (this.testing) this.stopTest();

				AIAgent.setSelectedVoiceModel(modelId);

				if (this.isModelSaved(model)) {

					Server.sendToServer({
						SelectUserVoice: {
							voiceId: modelId
						}
					});

				}

				if (!AIAgent.isVoiceModelLoaded(modelId)) {

					AIAgent.loadVoiceModelContent(modelId).catch(e => {

						this.error = `Model loading failed: ${e?.message || e}`;

					});

				}

			},
			deleteModel(e) {

				let model = this.selectedModel;

				if (!model || this.busy || !this.isModelSaved(model)) return;

				Broadcast.call('Show Confirm', [{
					caption: 'Delete voice model',
					message: `Are you sure you want to delete "${this.getModelTitle(model)}"?`,
					confirmButtonText: '&nbsp;&nbsp;Delete model',
					confirmButtonIcon: 'fa-trash',
					forceConfirm: e?.shiftKey,
					confirmCallback: () => {

						// Сервер удалит модель, выберет последнюю обученную из оставшихся (если удалена выбранная) и пришлёт обновлённый список
						Server.sendToServer({
							DeleteUserVoice: {
								voiceId: model.id
							}
						});

					}
				}]);

			},
			// Обучает новую микро-модель на включённых записях, добавляет её в список моделей и выбирает. Записи после обучения удаляются
			async train() {

				if (this.busy || !this.canTrain || this.modelsLimitReached) return;

				this.training = true;
				this.error = null;
				this.trainingProgress = {epoch: 0, epochs: 0, accuracy: null};

				// Сначала отрисовываем состояние обучения, иначе браузер зависает на подготовке данных без индикации
				await this.$nextTick();
				await new Promise(resolve => setTimeout(resolve, TRAIN_START_DELAY));

				try {

					let result = await AIAgent.trainVoiceModel(this.examples, progress => {

						this.trainingProgress = {epoch: progress.epoch, epochs: progress.epochs, accuracy: _.isFinite(progress.accuracy) ? progress.accuracy : null};

					});

					// Новая модель сразу становится выбранной и сохраняется на сервере в таблице user_voices
					AIAgent.saveVoiceModel(result.model);

					// Записи нужны только для обучения - удаляем
					this.examples = AIAgent.createEmptyExamples();

				} catch (e) {

					this.error = `Training failed: ${e?.message || e}`;

				}

				this.training = false;

			},
			async toggleTest() {

				if (this.testing) return this.stopTest();

				if (this.busy || !this.selectedModel) return;

				this.testing = true;
				this.error = null;
				this.scores = {};

				try {

					let transferRecognizer = await AIAgent.getVoiceModelRecognizer(this.selectedModel);

					if (!this.testing) return;

					this.testRecognizer = transferRecognizer;

					// Порог 0 - получаем уверенность модели на каждом окне, чтобы показывать живые индикаторы по всем фразам
					await AIAgent.startWordListening(transferRecognizer, result => this.processTestResult(result), {probabilityThreshold: 0});

					if (!this.testing) AIAgent.stopWordListening(transferRecognizer);

				} catch (e) {

					this.error = `Test failed: ${e?.message || e}`;
					this.testing = false;

				}

			},
			processTestResult(result) {

				if (!this.testing) return;

				this.scores = result.scores;

				// Подсвечиваем старт/стоп фразу только при уверенном распознавании
				if (result.label !== VOICE_LABELS.noise && result.score >= DETECTION_SCORE_THRESHOLD) {

					this.detectedLabel = result.label;

					clearTimeout(this.detectionTimeout);

					this.detectionTimeout = setTimeout(() => { this.detectedLabel = null; }, DETECTION_HIGHLIGHT_TIME);

				}

			},
			async stopTest() {

				this.testing = false;
				this.detectedLabel = null;

				clearTimeout(this.detectionTimeout);

				let transferRecognizer = this.testRecognizer;

				this.testRecognizer = null;

				try {

					await AIAgent.stopWordListening(transferRecognizer);

				} catch (e) {

					// Распознаватель мог уже быть остановлен - останавливать нечего

				}

			},
			formatDate(date) {

				return date ? new Date(date).toLocaleString() : '';

			},
			// Освобождаем микрофон и удаляем временные записи при закрытии окна
			cleanup() {

				if (this.testing) this.stopTest();

				this.examples = AIAgent.createEmptyExamples();

				this.transferRecognizer = null;

			}
		}
	}
</script>

<style lang="sass">

	.ai-chat-voice-training
		display: flex
		position: absolute
		z-index: 2000
		left: 0
		top: 0
		right: 0
		bottom: 0
		background-color: rgba(0, 0, 0, 0.6)
		transition: opacity 0.2s

		.separator
			padding-bottom: 20px
			border-bottom: 1px solid rgba(0, 0, 0, 0.3)
			margin-bottom: 10px

		.card
			width: 700px
			max-width: calc(100vw - 40px)
			margin: auto
			display: flex
			box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.8)
			transition: 0.2s

			.card-header
				i.fa
					margin-right: 8px

			.card-body
				max-height: calc(100vh - 160px)
				overflow-y: auto

			.description
				font-size: 13px
				margin-bottom: 15px

		.voice-training-status
			display: flex
			flex-direction: row
			align-items: center
			gap: 8px
			padding: 10px 0
			font-size: 13px

			&.error
				color: #f86c6b

		.voice-training-phrase
			padding: 10px 12px
			margin-bottom: 10px
			border: 1px solid #c8ced3
			border-radius: 0.25rem
			transition: background-color 0.2s, border-color 0.2s

			// Подсветка фразы, распознанной во время тестирования
			&.detected
				background-color: rgba(77, 189, 116, 0.2)
				border-color: #4dbd74

			&.recording
				border-color: #f86c6b

			.voice-training-phrase-header
				display: flex
				flex-direction: row
				align-items: center
				gap: 12px

			.voice-training-phrase-title
				flex: 1 1 auto
				display: flex
				flex-direction: column
				font-weight: bold
				font-size: 14px

				small
					font-weight: normal
					font-size: 12px

			.voice-training-phrase-count
				flex: 0 0 auto
				font-size: 12px
				color: #73818f
				white-space: nowrap

			.voice-training-record-button
				flex: 0 0 auto
				width: 130px

				i.fa
					margin-right: 6px

			&.recording
				.voice-training-record-button
					background-color: #f86c6b
					border-color: #f86c6b
					animation: ai-chat-voice-pulse 1s ease-in-out infinite

		.voice-training-examples
			display: flex
			flex-direction: row
			flex-wrap: wrap
			gap: 6px
			margin-top: 10px

		.voice-training-example
			display: flex
			flex-direction: row
			align-items: center
			gap: 6px
			padding: 3px 8px
			font-size: 12px
			border: 1px solid #c8ced3
			border-radius: 0.25rem
			cursor: pointer
			user-select: none

			&:hover
				border-color: #20a8d8

			&.disabled
				opacity: 0.5
				text-decoration: line-through

			.voice-training-example-delete
				margin-left: 4px
				color: #73818f

				&:hover
					color: #f86c6b

		.voice-training-examples-empty
			margin-top: 8px
			font-size: 12px
			color: #73818f

		.voice-training-score
			position: relative
			height: 16px
			margin-top: 10px
			background-color: #e4e7ea
			border-radius: 3px
			overflow: hidden
			font-size: 11px
			line-height: 16px

			.voice-training-score-bar
				position: absolute
				left: 0
				top: 0
				bottom: 0
				background-color: #20a8d8
				transition: width 0.15s

			span
				position: relative
				padding-left: 6px
				color: #23282c

		.voice-training-model
			padding-top: 5px

			.voice-training-model-info
				display: flex
				flex-direction: row
				align-items: center
				gap: 8px
				margin-bottom: 10px
				font-size: 13px

			.voice-training-model-select
				display: flex
				flex-direction: row
				align-items: center
				gap: 10px
				margin-bottom: 15px

				.input-select
					flex: 1 1 auto

				.voice-training-model-delete
					flex: 0 0 auto
					padding: 2px 10px

				label
					width: 200px

			.voice-training-model-actions
				display: flex
				flex-direction: row
				align-items: center
				gap: 10px

				.btn-dark
					margin-left: auto
					color: var(--white)

			.voice-training-test-hint
				font-size: 12px

	.ai-chat-voice-training.hiding
		opacity: 0

		.card
			transform: scale(0.9)

</style>
